import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Card, CardContent, TextField, Button, FormControl, 
  InputLabel, Select, MenuItem, CircularProgress, Alert
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import api from '../../utils/axios';
import { toast } from 'react-toastify';

const GlobalPushNotifications = () => {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [audience, setAudience] = useState('all');
  const [churchId, setChurchId] = useState('');
  const [churches, setChurches] = useState([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchChurches();
  }, []);

  const fetchChurches = async () => {
    try {
      const res = await api.get('/superadmin/churches');
      if (res.data.success) {
        setChurches(res.data.data);
      }
    } catch (err) {
      console.error('Failed to load churches for push notifications');
    }
  };

  const handleBroadcast = async () => {
    if (!title || !body) {
      toast.error('Title and body are required');
      return;
    }
    
    if (audience === 'specific_church' && !churchId) {
      toast.error('Please select a church');
      return;
    }

    if (!window.confirm(`Are you sure you want to broadcast this to ${audience}?`)) return;

    setSending(true);
    try {
      const res = await api.post('/superadmin/notifications/broadcast', {
        title,
        body,
        audience,
        churchId: audience === 'specific_church' ? churchId : undefined
      });

      if (res.data.success) {
        toast.success(`Sent successfully to ${res.data.recipientsCount} devices`);
        setTitle('');
        setBody('');
      }
    } catch (err) {
      toast.error('Failed to send broadcast');
    } finally {
      setSending(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Global Push Notifications</Typography>
      <Typography variant="body1" color="text.secondary" mb={3}>
        Send push notifications directly to user's mobile devices.
      </Typography>

      <Card sx={{ maxWidth: 600 }}>
        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 4 }}>
          <Alert severity="info">
            Only users who have opened the app and granted notification permissions will receive these messages.
          </Alert>

          <TextField 
            label="Notification Title" 
            variant="outlined" 
            fullWidth 
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g., New Feature Alert!"
          />

          <TextField 
            label="Message Body" 
            variant="outlined" 
            multiline
            rows={4}
            fullWidth 
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Type your message here..."
          />

          <FormControl fullWidth>
            <InputLabel>Target Audience</InputLabel>
            <Select
              value={audience}
              label="Target Audience"
              onChange={(e) => setAudience(e.target.value)}
            >
              <MenuItem value="all">All Users (Global)</MenuItem>
              <MenuItem value="pro">Only PRO Users</MenuItem>
              <MenuItem value="specific_church">Specific Church</MenuItem>
            </Select>
          </FormControl>

          {audience === 'specific_church' && (
            <FormControl fullWidth>
              <InputLabel>Select Church</InputLabel>
              <Select
                value={churchId}
                label="Select Church"
                onChange={(e) => setChurchId(e.target.value)}
              >
                {churches.map(c => (
                  <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Button 
            variant="contained" 
            size="large" 
            color="primary"
            startIcon={sending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
            onClick={handleBroadcast}
            disabled={sending}
            sx={{ mt: 2 }}
          >
            {sending ? 'Sending...' : 'Broadcast Notification'}
          </Button>

        </CardContent>
      </Card>
    </Box>
  );
};

export default GlobalPushNotifications;
